/**
 * Spreads `sessionsPerWeek` watering events across a 7-day week, respecting
 * a city watering-restriction pattern (e.g. Mon/Wed/Fri only). Used by the
 * live preview chart on /tools/watering-schedule.
 *
 * Pure: no engine inputs beyond what's already returned by
 * `calculateWateringSchedule`. The engine itself only takes a boolean
 * `waterRestrictions` flag (it surfaces a tip line) — actual day picking
 * lives here so the chart can redraw immediately when the user changes
 * the restriction chip.
 */

export type RestrictionId = "none" | "mwf" | "tts" | "odd" | "even" | "wkdy"

export interface RestrictionPattern {
  id: RestrictionId
  label: string
  /** Day-of-week indices (0 = Sun, 6 = Sat). */
  days: number[]
}

export const RESTRICTIONS: RestrictionPattern[] = [
  { id: "none", label: "None", days: [0, 1, 2, 3, 4, 5, 6] },
  { id: "mwf", label: "Mon / Wed / Fri", days: [1, 3, 5] },
  { id: "tts", label: "Tue / Thu / Sat", days: [2, 4, 6] },
  { id: "odd", label: "Odd days", days: [1, 3, 5, 0] },
  { id: "even", label: "Even days", days: [2, 4, 6] },
  { id: "wkdy", label: "Weekdays", days: [1, 2, 3, 4, 5] },
]

export interface ScheduleDay {
  day: number
  on: boolean
  time: string
  gallons: number
  minutes: number
}

interface BuildScheduleDaysInput {
  sessionsPerWeek: number
  gallonsPerSession: number
  runMinutes: number
  restrictionId: RestrictionId
  /** Display string, e.g. "6:00 AM". Defaults to "6:00 AM". */
  time?: string
}

/**
 * Returns a 7-element array (Sun → Sat) where `on` days have non-zero
 * gallons/minutes. Picks evenly-spaced indices into the restriction's
 * allowed-days list so two-session weeks land on opposite ends rather
 * than back-to-back.
 */
export function buildScheduleDays({
  sessionsPerWeek,
  gallonsPerSession,
  runMinutes,
  restrictionId,
  time = "6:00 AM",
}: BuildScheduleDaysInput): ScheduleDay[] {
  const restr = RESTRICTIONS.find(r => r.id === restrictionId) ?? RESTRICTIONS[0]
  const events = Math.min(sessionsPerWeek, restr.days.length)
  const chosen = new Set<number>()
  if (events > 0) {
    for (let i = 0; i < events; i++) {
      const idx = Math.floor((i * restr.days.length) / events)
      chosen.add(restr.days[idx])
    }
  }
  return [0, 1, 2, 3, 4, 5, 6].map(d => ({
    day: d,
    on: chosen.has(d),
    time,
    gallons: chosen.has(d) ? gallonsPerSession : 0,
    minutes: chosen.has(d) ? runMinutes : 0,
  }))
}
